import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, ChartLineUp, Users, FileText, Bell, MapTrifold,
  Tree, HandHeart, Briefcase, Buildings, ListChecks, ArrowRight,
  ArrowLeft, CheckCircle, Database, LockKey, Globe, Sparkle,
  Lightning, ChatsTeardrop, Target, ClipboardText, Eye,
  CaretLeft, CaretRight, House, ArrowsClockwise,
  CloudArrowUp, WhatsappLogo, EnvelopeSimple, PaperPlaneTilt,
  Wrench, ArrowCounterClockwise, Cpu, Stack,
  Microphone, FilePdf, MagicWand, Brain, Megaphone, Newspaper,
  X as CloseIcon,
} from "@phosphor-icons/react";

const Pill = ({ children, tone = "orange" }) => {
  const tones = {
    orange: "border-brand-orange-400/40 bg-brand-orange-500/10 text-brand-orange-300",
    green: "border-brand-green-400/40 bg-brand-green-500/10 text-brand-green-300",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border ${tones[tone]} px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]`}>
      <Sparkle size={10} weight="fill" />{children}
    </span>
  );
};

const Stat = ({ value, label }) => (
  <div>
    <div className="font-display text-4xl md:text-5xl font-bold text-white">{value}</div>
    <div className="text-xs uppercase tracking-[0.2em] text-brand-orange-300/80 mt-1">{label}</div>
  </div>
);

const Feature = ({ icon: Icon, title, children, tone = "orange" }) => {
  const tones = {
    orange: "from-brand-orange-500/20 to-brand-orange-500/0 border-brand-orange-400/30 text-brand-orange-300",
    green: "from-brand-green-500/20 to-brand-green-500/0 border-brand-green-400/30 text-brand-green-300",
    sky: "from-sky-500/20 to-sky-500/0 border-sky-400/30 text-sky-300",
    rose: "from-rose-500/20 to-rose-500/0 border-rose-400/30 text-rose-300",
    violet: "from-violet-500/20 to-violet-500/0 border-violet-400/30 text-violet-300",
  };
  return (
    <div className={`relative rounded-2xl border bg-gradient-to-br ${tones[tone]} p-5`}>
      <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-slate-900/60 border border-white/10 mb-3">
        <Icon size={22} weight="duotone" />
      </div>
      <h3 className="font-display text-base font-semibold text-white mb-1.5">{title}</h3>
      <p className="text-sm text-slate-300 leading-relaxed">{children}</p>
    </div>
  );
};

const QABlock = ({ icon: Icon, q, a, tone = "orange" }) => {
  const iconTones = { orange: "text-brand-orange-400", green: "text-brand-green-400", sky: "text-sky-400" };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <div className="flex items-start gap-3">
        <Icon size={26} weight="duotone" className={`${iconTones[tone]} mt-0.5 flex-shrink-0`} />
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-1">Pregunta</div>
          <h4 className="font-display text-base font-semibold text-white mb-2">{q}</h4>
          <p className="text-sm text-slate-300 leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// SLIDES
// =================================================================
function SlidePortada() {
  return (
    <div className="h-full w-full max-w-[1500px] mx-auto grid md:grid-cols-12 gap-10 items-center px-8 md:px-16 py-10">
      <div className="md:col-span-7 space-y-6">
        <Pill>Presentación</Pill>
        <img src="/assets/sigsaop-logo-light.svg?v=5" alt="SIGSAOP" className="h-28 md:h-36 w-auto object-contain" />
        <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight">
          Toda tu gestión de <span className="text-brand-orange-400">relaciones</span> en una sola plataforma.
        </h1>
        <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl">
          Compromisos, comunidades, autoridades, proyectos, RCA, asuntos públicos y agenda institucional — con trazabilidad, alertas automáticas y reportes para directorio.
        </p>
        <div className="grid grid-cols-3 gap-6 pt-6 border-t border-white/10 max-w-lg">
          <Stat value="100%" label="Trazable" />
          <Stat value="Auto" label="Alertas" />
          <Stat value="AWS" label="Cloud seguro" />
        </div>
      </div>
      <div className="md:col-span-5">
        <div className="relative">
          <div className="absolute -inset-8 bg-gradient-to-br from-brand-orange-400/25 via-brand-green-400/15 to-sky-400/20 rounded-3xl blur-3xl" />
          <div className="relative rounded-2xl border border-white/10 bg-slate-900/80 backdrop-blur-xl p-5 shadow-2xl">
            <div className="flex items-center gap-1.5 mb-4">
              <div className="w-2.5 h-2.5 rounded-full bg-rose-400/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-brand-green-400/60" />
              <div className="text-[10px] text-slate-500 ml-2 font-mono">sigsaop.cl/dashboard</div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <MiniKpi label="Compromisos" value="284" />
              <MiniKpi label="Cumplidos" value="76%" tone="green" />
              <MiniKpi label="Atrasados" value="12" tone="rose" />
            </div>
            <div className="rounded-lg border border-white/10 bg-slate-950/60 p-3 space-y-2">
              {[["Salud", 82, "bg-brand-green-400"], ["Educación", 64, "bg-sky-400"], ["Infraestructura", 47, "bg-brand-orange-400"], ["Medio ambiente", 91, "bg-violet-400"]].map(([k, v, c]) => (
                <div key={k}>
                  <div className="flex justify-between text-[11px] mb-1">
                    <span className="text-slate-300">{k}</span>
                    <span className="text-slate-400 font-mono">{v}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div className={`h-full ${c}`} style={{ width: `${v}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SlideProblema() {
  return (
    <div className="h-full w-full px-8 md:px-16 py-10 flex flex-col justify-center items-center text-center">
      <Pill>El problema</Pill>
      <h2 className="font-display text-4xl md:text-6xl font-bold mt-5 mb-4 leading-[1.05] max-w-5xl">
        Excel no alcanza para gestionar <span className="text-rose-400">relaciones críticas</span>.
      </h2>
      <p className="text-base md:text-lg text-slate-400 mb-12 max-w-3xl">
        Lo que pierdes con la gestión tradicional — y lo que ganas al centralizar en SIGSAOP.
      </p>
      <div className="grid md:grid-cols-2 gap-8 w-full max-w-6xl mx-auto items-stretch">
        {/* ANTES */}
        <div className="rounded-2xl border border-rose-400/30 bg-gradient-to-br from-rose-500/10 to-transparent p-7 text-left">
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex w-11 h-11 rounded-xl bg-rose-500/20 border border-rose-400/30 items-center justify-center">
              <FileText size={22} weight="duotone" className="text-rose-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-rose-300">Hoy</div>
              <div className="font-display text-xl font-bold text-white">Excel + correos + carpetas</div>
            </div>
          </div>
          <ul className="space-y-3 text-sm text-slate-300">
            <li className="flex items-start gap-2"><CloseIcon size={16} weight="bold" className="text-rose-400 mt-0.5 flex-shrink-0" /> Excels duplicados sin versión vigente</li>
            <li className="flex items-start gap-2"><CloseIcon size={16} weight="bold" className="text-rose-400 mt-0.5 flex-shrink-0" /> Compromisos vencen sin que nadie se entere</li>
            <li className="flex items-start gap-2"><CloseIcon size={16} weight="bold" className="text-rose-400 mt-0.5 flex-shrink-0" /> Información dispersa o perdida en un PC</li>
            <li className="flex items-start gap-2"><CloseIcon size={16} weight="bold" className="text-rose-400 mt-0.5 flex-shrink-0" /> Sin evidencias o archivos visibles</li>
            <li className="flex items-start gap-2"><CloseIcon size={16} weight="bold" className="text-rose-400 mt-0.5 flex-shrink-0" /> Cero trazabilidad en auditorías</li>
            <li className="flex items-start gap-2"><CloseIcon size={16} weight="bold" className="text-rose-400 mt-0.5 flex-shrink-0" /> Reportes hechos a mano y desactualizados</li>
          </ul>
        </div>
        {/* CON SIGSAOP */}
        <div className="rounded-2xl border-2 border-brand-green-400/40 bg-gradient-to-br from-brand-green-500/15 to-transparent p-7 text-left shadow-2xl shadow-brand-green-500/10">
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex w-11 h-11 rounded-xl bg-brand-green-500/20 border border-brand-green-400/30 items-center justify-center">
              <ShieldCheck size={22} weight="duotone" className="text-brand-green-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-green-300">Con SIGSAOP</div>
              <div className="font-display text-xl font-bold text-white">Plataforma única</div>
            </div>
          </div>
          <ul className="space-y-3 text-sm text-slate-200">
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-green-400 mt-0.5 flex-shrink-0" /> Una sola fuente de verdad, siempre vigente</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-green-400 mt-0.5 flex-shrink-0" /> Alertas automáticas en los plazos que definas</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-green-400 mt-0.5 flex-shrink-0" /> Memoria institucional permanente</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-green-400 mt-0.5 flex-shrink-0" /> Logs completos para auditoría</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-green-400 mt-0.5 flex-shrink-0" /> Reportes ejecutivos en 1 click</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function SlideParaQueSirve() {
  const modules = [
    { icon: Tree, label: "RCA / Ambiental", tone: "green" },
    { icon: HandHeart, label: "Comunidades", tone: "green" },
    { icon: Buildings, label: "Asuntos Públicos", tone: "orange" },
    { icon: Newspaper, label: "Parlamentarios", tone: "orange" },
    { icon: ChatsTeardrop, label: "Reuniones", tone: "violet" },
    { icon: Briefcase, label: "Proyectos", tone: "sky" },
    { icon: ListChecks, label: "Compromisos", tone: "orange" },
    { icon: Target, label: "Indicadores", tone: "rose" },
    { icon: MapTrifold, label: "Mapa territorial", tone: "sky" },
    { icon: ChartLineUp, label: "Reportería", tone: "green" },
  ];
  return (
    <div className="h-full w-full px-8 md:px-16 py-10 flex flex-col justify-center items-center text-center">
      <Pill>Para qué sirve</Pill>
      <h2 className="font-display text-4xl md:text-6xl font-bold mt-5 mb-3 leading-[1.05] max-w-5xl">
        Una plataforma. <span className="text-brand-orange-400">10 módulos.</span>
      </h2>
      <p className="text-base md:text-lg text-slate-400 mb-10 max-w-3xl">
        Todo lo que antes vivía disperso entre planillas, correos y carpetas — ahora en un solo lugar.
      </p>
      <div className="relative w-full max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {modules.map((m) => <ModuleChip key={m.label} {...m} />)}
        </div>
      </div>
      <div className="mt-10 text-sm text-slate-400 max-w-4xl">
        No solo medio ambiente y comunidades — también <strong className="text-white">asuntos públicos</strong>, <strong className="text-white">parlamentarios</strong>, <strong className="text-white">gremios</strong>, <strong className="text-white">prensa</strong> y toda tu agenda institucional.
      </div>
    </div>
  );
}

function SlidePropuestaValor() {
  return (
    <div className="h-full w-full px-8 md:px-16 py-10 flex flex-col justify-center items-center text-center">
      <Pill>Propuesta de valor</Pill>
      <h2 className="font-display text-4xl md:text-5xl font-bold mt-5 mb-3 leading-tight max-w-4xl">
        Convertimos tu gestión en <span className="text-brand-orange-400">datos accionables</span>.
      </h2>
      <p className="text-base md:text-lg text-slate-400 mb-10 max-w-3xl">
        Dos pilares — un mismo resultado: control, evidencia y memoria institucional.
      </p>
      <div className="grid md:grid-cols-2 gap-6 w-full max-w-6xl mx-auto text-left">
        <div className="rounded-2xl border border-brand-orange-400/30 bg-gradient-to-br from-brand-orange-500/10 to-transparent p-7">
          <ShieldCheck size={32} weight="duotone" className="text-brand-orange-400 mb-4" />
          <h3 className="font-display text-2xl font-bold mb-3">Accountability real</h3>
          <p className="text-slate-300 mb-4 text-sm leading-relaxed">
            Cada acción queda registrada con autor, fecha y evidencia. Tu equipo cumple con datos verificables ante auditorías, directorio y comunidad.
          </p>
          <ul className="space-y-2 text-sm text-slate-200">
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-orange-400 mt-0.5" />Responsable identificado en cada compromiso</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-orange-400 mt-0.5" />Histórico completo de avances</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-orange-400 mt-0.5" />Evidencias adjuntas con fecha y autor</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-orange-400 mt-0.5" />Alertas automáticas 30 y 3 días antes</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-brand-green-400/30 bg-gradient-to-br from-brand-green-500/10 to-transparent p-7">
          <Database size={32} weight="duotone" className="text-brand-green-400 mb-4" />
          <h3 className="font-display text-2xl font-bold mb-3">Repositorio único</h3>
          <p className="text-slate-300 mb-4 text-sm leading-relaxed">
            Toda la memoria social y ambiental de tu organización en un solo lugar. Búsqueda transversal, descargable, auditable.
          </p>
          <ul className="space-y-2 text-sm text-slate-200">
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-green-400 mt-0.5" />Actas con texto enriquecido + fotos</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-green-400 mt-0.5" />Archivos en cloud con previsualización</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-green-400 mt-0.5" />Mapas geográficos con capas</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-brand-green-400 mt-0.5" />Vinculación cruzada entre entidades</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function SlideComoFunciona() {
  const steps = [
    { n: "1", t: "Registra", d: "Comunidades, autoridades, parlamentarios, gremios.", icon: Users },
    { n: "2", t: "Crea", d: "Compromisos, proyectos, vinculaciones.", icon: ListChecks },
    { n: "3", t: "Documenta", d: "Actas, RCA, evidencias.", icon: ClipboardText },
    { n: "4", t: "Mide", d: "Alertas, dashboard, reportes.", icon: ChartLineUp },
  ];
  return (
    <div className="h-full w-full px-8 md:px-16 py-10 flex flex-col justify-center items-center text-center">
      <Pill>Cómo funciona</Pill>
      <h2 className="font-display text-4xl md:text-6xl font-bold mt-5 mb-3 leading-[1.05] max-w-5xl">
        Cuatro pasos para tomar el <span className="text-brand-orange-400">control</span>.
      </h2>
      <p className="text-base md:text-lg text-slate-400 mb-12 max-w-3xl">
        Un flujo simple — desde el primer dato hasta el reporte para directorio.
      </p>
      <div className="relative grid md:grid-cols-4 gap-6 w-full max-w-6xl mx-auto">
        {/* connector line */}
        <div className="hidden md:block absolute top-[58px] left-[8%] right-[8%] h-px bg-gradient-to-r from-brand-orange-500 via-brand-green-500 to-sky-500 opacity-40" />
        {steps.map((s) => (
          <div key={s.n} className="relative">
            <div className="w-[120px] h-[120px] rounded-full mx-auto bg-slate-900 border-2 border-brand-orange-400/40 flex items-center justify-center relative z-10">
              <s.icon size={44} weight="duotone" className="text-brand-orange-400" />
              <div className="absolute -top-2 -right-2 w-9 h-9 rounded-full bg-brand-orange-500 text-white font-display font-bold flex items-center justify-center text-base shadow-lg shadow-brand-orange-500/40">{s.n}</div>
            </div>
            <h4 className="text-center font-display text-xl font-semibold mt-5 mb-1">{s.t}</h4>
            <p className="text-center text-sm text-slate-400">{s.d}</p>
          </div>
        ))}
      </div>
      <div className="mt-14 flex flex-wrap items-center justify-center gap-3 w-full max-w-3xl mx-auto">
        <Capability icon={LockKey} label="Multi-tenant seguro" />
        <Capability icon={Globe} label="Acceso web 24/7" />
        <Capability icon={Lightning} label="Listo en horas" />
      </div>
    </div>
  );
}

function SlidePersonalizacion() {
  return (
    <div className="h-full w-full px-8 md:px-16 py-10 flex flex-col justify-center items-center text-center">
      <Pill>Personalización</Pill>
      <h2 className="font-display text-4xl md:text-6xl font-bold mt-5 mb-3 leading-[1.05] max-w-5xl">
        A medida. <span className="text-brand-orange-400">Sin costos extra.</span>
      </h2>
      <p className="text-base md:text-lg text-slate-400 mb-12 max-w-3xl">
        SIGSAOP se adapta a tu organización, no al revés.
      </p>
      <div className="grid md:grid-cols-3 gap-6 w-full max-w-6xl mx-auto text-left">
        <BigStatCard icon={Wrench} stat="100%" label="Customizable" tone="orange"
          sub="Campos, secciones, ejes y categorías a tu medida." />
        <BigStatCard icon={ArrowsClockwise} stat="$0" label="Cambios estructurales" tone="green"
          sub="Si el sistema ya lo contempla, no se cobra." />
        <BigStatCard icon={Stack} stat="∞" label="Perfiles configurables" tone="sky"
          sub="Permisos granulares por módulo, comunidad o proyecto." />
      </div>
    </div>
  );
}

function SlideSeguridad() {
  return (
    <div className="h-full w-full px-8 md:px-16 py-10 flex flex-col justify-center items-center text-center">
      <Pill>Seguridad e infraestructura</Pill>
      <h2 className="font-display text-4xl md:text-6xl font-bold mt-5 mb-3 leading-[1.05] max-w-5xl">
        Blindados en <span className="text-brand-orange-400">AWS</span>.
      </h2>
      <p className="text-base md:text-lg text-slate-400 mb-12 max-w-3xl">
        La misma infraestructura que usan los bancos y los gobiernos.
      </p>
      <div className="grid md:grid-cols-12 gap-8 items-center w-full max-w-6xl mx-auto text-left">
        <div className="md:col-span-5">
          <div className="relative rounded-2xl border-2 border-brand-orange-400/40 bg-gradient-to-br from-brand-orange-500/15 to-transparent p-8 text-center">
            <CloudArrowUp size={72} weight="duotone" className="text-brand-orange-400 mx-auto mb-4" />
            <div className="font-display text-3xl font-bold">Amazon Web Services</div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-orange-300 mt-2">Cloud líder mundial</div>
          </div>
        </div>
        <div className="md:col-span-7 space-y-3">
          <SecurityRow icon={LockKey} text="Cifrado de extremo a extremo en tránsito y reposo" />
          <SecurityRow icon={ShieldCheck} text="Aislamiento multi-tenant por organización" />
          <SecurityRow icon={Eye} text="Logs de auditoría completos" />
          <SecurityRow icon={Globe} text="HTTPS exclusivo · JWT con expiración" />
          <SecurityRow icon={Cpu} text="On-premise opcional si tu política lo requiere" />
        </div>
      </div>
    </div>
  );
}

function SlideRespaldoYRecuperacion() {
  return (
    <div className="h-full w-full px-8 md:px-16 py-10 flex flex-col justify-center items-center text-center">
      <Pill>Respaldo y continuidad</Pill>
      <h2 className="font-display text-4xl md:text-6xl font-bold mt-5 mb-3 leading-[1.05] max-w-5xl">
        Tu información, <span className="text-brand-green-400">para siempre</span>.
      </h2>
      <p className="text-base md:text-lg text-slate-400 mb-12 max-w-3xl">
        Backups automáticos, retención configurable y recuperación punto a punto.
      </p>
      <div className="grid md:grid-cols-4 gap-5 w-full max-w-7xl mx-auto text-left">
        <BigStatCard icon={Database} stat="24/7" label="Backups automáticos" tone="green"
          sub="Redundancia geográfica en AWS." />
        <BigStatCard icon={ArrowCounterClockwise} stat="∞" label="Retención" tone="orange"
          sub="El cliente define el horizonte temporal." />
        <BigStatCard icon={ShieldCheck} stat="99.999%" label="Durabilidad" tone="sky"
          sub="Solo se pierde si el cliente la elimina." />
        <BigStatCard icon={ArrowsClockwise} stat="100%" label="Recuperable" tone="green"
          sub="Soft-delete + restauración point-in-time." />
      </div>
    </div>
  );
}

function SlideComunicacion() {
  return (
    <div className="h-full w-full px-8 md:px-16 py-10 flex flex-col justify-center items-center text-center">
      <Pill>Comunicación multicanal</Pill>
      <h2 className="font-display text-4xl md:text-6xl font-bold mt-5 mb-3 leading-[1.05] max-w-5xl">
        Avisos a través de los <span className="text-brand-orange-400">medios que definas con tu equipo</span>.
      </h2>
      <p className="text-base md:text-lg text-slate-400 mb-10 max-w-3xl">
        Una sola alerta — los canales que cada persona usa todos los días.
      </p>
      <div className="relative w-full max-w-6xl mx-auto">
        {/* central hub */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-brand-orange-400 to-brand-orange-600 flex items-center justify-center shadow-2xl shadow-brand-orange-500/40">
            <Bell size={56} weight="duotone" className="text-white" />
          </div>
          <div className="mt-4 text-center">
            <div className="font-display text-2xl font-bold">SIGSAOP</div>
            <div className="text-xs uppercase tracking-[0.18em] text-brand-orange-300 mt-1">Plazos configurables</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-left">
          <ChannelCard icon={EnvelopeSimple} title="Correo" desc="Múltiples destinatarios" />
          <ChannelCard icon={WhatsappLogo} title="WhatsApp" desc="Plantillas a responsables" tone="green" />
          <ChannelCard icon={PaperPlaneTilt} title="Telegram" desc="Bots y canales" tone="sky" />
          <ChannelCard icon={ChatsTeardrop} title="SMS" desc="Zonas baja conectividad" tone="violet" />
        </div>
      </div>
    </div>
  );
}

function SlideVisibilidad() {
  return (
    <div className="h-full w-full px-8 md:px-16 py-10 flex flex-col justify-center items-center text-center">
      <Pill>Visibilidad y reportes</Pill>
      <h2 className="font-display text-4xl md:text-6xl font-bold mt-5 mb-3 leading-[1.05] max-w-5xl">
        Comparte lo justo con <span className="text-brand-orange-400">cada audiencia</span>.
      </h2>
      <p className="text-base md:text-lg text-slate-400 mb-12 max-w-3xl">
        El mismo dato — tres niveles de detalle. Sin filtraciones, sin duplicar trabajo.
      </p>
      <div className="grid md:grid-cols-3 gap-5 w-full max-w-6xl mx-auto text-left">
        <AudienceTier icon={Briefcase} stat="1 click" label="Directorio" sub="KPIs ejecutivos, exportables a PDF y Excel." tone="orange" />
        <AudienceTier icon={Users} stat="Perfiles" label="Equipo" sub="Permisos granulares por módulo y entidad." tone="sky" />
        <AudienceTier icon={HandHeart} stat="Público" label="Comunidad" sub="Vistas simples y transparentes del avance." tone="green" />
      </div>
    </div>
  );
}

function SlideServicio() {
  return (
    <div className="h-full w-full px-8 md:px-16 py-10 flex flex-col justify-center items-center text-center">
      <Pill>Servicio y soporte</Pill>
      <h2 className="font-display text-4xl md:text-6xl font-bold mt-5 mb-3 leading-[1.05] max-w-5xl">
        Un <span className="text-brand-orange-400">partner</span>, no un proveedor.
      </h2>
      <p className="text-base md:text-lg text-slate-400 mb-12 max-w-3xl">
        Te acompañamos desde la implementación hasta el día a día — sin tarifas sorpresa.
      </p>
      <div className="grid md:grid-cols-3 gap-5 w-full max-w-6xl mx-auto text-left">
        <BigStatCard icon={Lightning} stat="24/7" label="Carga de información" tone="orange"
          sub="Servicio continuo según el SLA que requieras." />
        <BigStatCard icon={MagicWand} stat="Llave en mano" label="Implementación" tone="green"
          sub="Migramos desde tus Excels y capacitamos al equipo." />
        <BigStatCard icon={Brain} stat="Siempre" label="Día a día" tone="sky"
          sub="Soporte permanente sin tarifas sorpresa." />
      </div>
    </div>
  );
}

function SlideCTA() {
  const steps = [
    { n: "01", t: "Diagnóstico", d: "Revisamos cómo gestionas hoy y qué Excels migrar.", icon: ClipboardText, tone: "orange" },
    { n: "02", t: "Configuración", d: "Personalizamos campos, ejes y permisos a tu medida.", icon: Wrench, tone: "green" },
    { n: "03", t: "Migración", d: "Cargamos tu información histórica al sistema.", icon: CloudArrowUp, tone: "sky" },
    { n: "04", t: "Capacitación", d: "Entrenamos a tu equipo en 2 a 4 sesiones.", icon: Users, tone: "violet" },
    { n: "05", t: "Acompañamiento", d: "Soporte continuo, mejoras y nuevos módulos.", icon: HandHeart, tone: "orange" },
  ];
  return (
    <div className="h-full w-full px-8 md:px-16 py-10 flex flex-col justify-center items-center text-center">
      <Pill>Próximos pasos</Pill>
      <h2 className="font-display text-4xl md:text-6xl font-bold mt-5 mb-3 leading-[1.05] max-w-5xl">
        Así se ve <span className="text-brand-orange-400">implementar SIGSAOP</span>.
      </h2>
      <p className="text-base md:text-lg text-slate-400 mb-14 max-w-3xl">
        Cinco pasos — de tu primer Excel a una plataforma viva con todo tu equipo.
      </p>
      <div className="relative w-full max-w-7xl mx-auto">
        {/* connector line */}
        <div className="hidden md:block absolute top-[44px] left-[10%] right-[10%] h-px bg-gradient-to-r from-brand-orange-500 via-brand-green-500 to-violet-500 opacity-40" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {steps.map((s) => {
            const tones = {
              orange: { ring: "border-brand-orange-400/50", icon: "text-brand-orange-400", badge: "bg-brand-orange-500 shadow-brand-orange-500/40" },
              green: { ring: "border-brand-green-400/50", icon: "text-brand-green-400", badge: "bg-brand-green-500 shadow-brand-green-500/40" },
              sky: { ring: "border-sky-400/50", icon: "text-sky-400", badge: "bg-sky-500 shadow-sky-500/40" },
              violet: { ring: "border-violet-400/50", icon: "text-violet-400", badge: "bg-violet-500 shadow-violet-500/40" },
            }[s.tone];
            return (
              <div key={s.n} className="relative">
                <div className={`w-[88px] h-[88px] rounded-full mx-auto bg-slate-900 border-2 ${tones.ring} flex items-center justify-center relative z-10`}>
                  <s.icon size={36} weight="duotone" className={tones.icon} />
                  <div className={`absolute -top-2 -right-2 px-2 h-7 rounded-full text-white font-mono font-bold flex items-center justify-center text-xs shadow-lg ${tones.badge}`}>{s.n}</div>
                </div>
                <h4 className="text-center font-display text-lg font-semibold mt-4 mb-1 text-white">{s.t}</h4>
                <p className="text-center text-xs text-slate-400 leading-relaxed">{s.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// =================================================================
// Helpers
// =================================================================
function MiniKpi({ label, value, tone = "slate" }) {
  const tones = {
    slate: "text-white",
    green: "text-brand-green-300",
    rose: "text-rose-300",
  };
  return (
    <div className="rounded-lg border border-white/10 bg-slate-950/60 p-2.5">
      <div className="text-[9px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className={`font-display text-xl font-bold ${tones[tone]} mt-1`}>{value}</div>
    </div>
  );
}

function ProblemCard({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-rose-500/10 border border-rose-400/20 text-rose-300 mb-4">
        <Icon size={20} weight="duotone" />
      </div>
      <h3 className="font-display text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{children}</p>
    </div>
  );
}

function Capability({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.02] px-3.5 py-3">
      <Icon size={18} weight="duotone" className="text-brand-orange-400" />
      <span className="text-sm text-slate-200">{label}</span>
    </div>
  );
}

function ChannelCard({ icon: Icon, title, desc, tone = "orange" }) {
  const tones = {
    orange: "border-brand-orange-400/30 bg-brand-orange-500/10 text-brand-orange-300",
    green: "border-brand-green-400/30 bg-brand-green-500/10 text-brand-green-300",
    sky: "border-sky-400/30 bg-sky-500/10 text-sky-300",
    violet: "border-violet-400/30 bg-violet-500/10 text-violet-300",
  };
  return (
    <div className={`rounded-2xl border p-5 ${tones[tone]}`}>
      <Icon size={28} weight="duotone" className="mb-3" />
      <div className="font-display font-semibold text-white">{title}</div>
      <div className="text-xs text-slate-300 mt-1">{desc}</div>
    </div>
  );
}

function BigStatCard({ icon: Icon, stat, label, sub, tone = "orange" }) {
  const tones = {
    orange: { border: "border-brand-orange-400/30", bg: "from-brand-orange-500/15 to-transparent", icon: "text-brand-orange-400", stat: "text-brand-orange-300" },
    green: { border: "border-brand-green-400/30", bg: "from-brand-green-500/15 to-transparent", icon: "text-brand-green-400", stat: "text-brand-green-300" },
    sky: { border: "border-sky-400/30", bg: "from-sky-500/15 to-transparent", icon: "text-sky-400", stat: "text-sky-300" },
    rose: { border: "border-rose-400/30", bg: "from-rose-500/15 to-transparent", icon: "text-rose-400", stat: "text-rose-300" },
    amber: { border: "border-amber-400/30", bg: "from-amber-500/15 to-transparent", icon: "text-amber-400", stat: "text-amber-300" },
    violet: { border: "border-violet-400/30", bg: "from-violet-500/15 to-transparent", icon: "text-violet-400", stat: "text-violet-300" },
  };
  const t = tones[tone] || tones.orange;
  return (
    <div className={`relative rounded-2xl border ${t.border} bg-gradient-to-br ${t.bg} p-6 flex flex-col`}>
      <Icon size={32} weight="duotone" className={`${t.icon} mb-4`} />
      <div className={`font-display text-5xl md:text-6xl font-bold ${t.stat} leading-none`}>{stat}</div>
      <div className="text-sm uppercase tracking-[0.18em] text-white mt-3 font-semibold">{label}</div>
      {sub && <div className="text-sm text-slate-400 mt-2 leading-relaxed">{sub}</div>}
    </div>
  );
}

function ModuleChip({ icon: Icon, label, tone = "orange" }) {
  const tones = {
    orange: "border-brand-orange-400/30 bg-brand-orange-500/10 text-brand-orange-300",
    green: "border-brand-green-400/30 bg-brand-green-500/10 text-brand-green-300",
    sky: "border-sky-400/30 bg-sky-500/10 text-sky-300",
    violet: "border-violet-400/30 bg-violet-500/10 text-violet-300",
    rose: "border-rose-400/30 bg-rose-500/10 text-rose-300",
  };
  return (
    <div className={`group rounded-xl border ${tones[tone]} p-4 flex flex-col items-center text-center gap-2.5 transition hover:scale-[1.03] hover:shadow-lg`}>
      <Icon size={32} weight="duotone" />
      <div className="text-sm font-semibold text-white leading-tight">{label}</div>
    </div>
  );
}

function SecurityRow({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5">
      <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-brand-orange-500/15 border border-brand-orange-400/30 text-brand-orange-300 flex-shrink-0">
        <Icon size={18} weight="duotone" />
      </div>
      <span className="text-sm text-slate-200">{text}</span>
    </div>
  );
}

function AudienceTier({ icon: Icon, stat, label, sub, tone = "orange" }) {
  const tones = {
    orange: { border: "border-brand-orange-400/30", bg: "from-brand-orange-500/15 to-transparent", icon: "text-brand-orange-400", stat: "text-brand-orange-300" },
    green: { border: "border-brand-green-400/30", bg: "from-brand-green-500/15 to-transparent", icon: "text-brand-green-400", stat: "text-brand-green-300" },
    sky: { border: "border-sky-400/30", bg: "from-sky-500/15 to-transparent", icon: "text-sky-400", stat: "text-sky-300" },
  };
  const t = tones[tone] || tones.orange;
  return (
    <div className={`rounded-2xl border ${t.border} bg-gradient-to-br ${t.bg} p-6 flex flex-col`}>
      <Icon size={28} weight="duotone" className={`${t.icon} mb-4`} />
      <div className={`font-display text-3xl md:text-4xl font-bold ${t.stat} leading-none`}>{stat}</div>
      <div className="text-sm uppercase tracking-[0.18em] text-white mt-3 font-semibold">{label}</div>
      {sub && <div className="text-sm text-slate-400 mt-2 leading-relaxed">{sub}</div>}
    </div>
  );
}

// =================================================================
// MAIN COMPONENT
// =================================================================
const SLIDES = [
  { id: 1, title: "Portada", component: SlidePortada },
  { id: 2, title: "Problema", component: SlideProblema },
  { id: 3, title: "Para qué sirve", component: SlideParaQueSirve },
  { id: 4, title: "Propuesta de valor", component: SlidePropuestaValor },
  { id: 5, title: "Cómo funciona", component: SlideComoFunciona },
  { id: 6, title: "Personalización", component: SlidePersonalizacion },
  { id: 7, title: "Seguridad", component: SlideSeguridad },
  { id: 8, title: "Respaldo", component: SlideRespaldoYRecuperacion },
  { id: 9, title: "Comunicación", component: SlideComunicacion },
  { id: 10, title: "Visibilidad", component: SlideVisibilidad },
  { id: 11, title: "Servicio", component: SlideServicio },
  { id: 12, title: "Próximos pasos", component: SlideCTA },
];

export default function Presentacion() {
  const [idx, setIdx] = useState(0);
  const [showIndex, setShowIndex] = useState(false);
  const total = SLIDES.length;

  const next = useCallback(() => setIdx((i) => Math.min(i + 1, total - 1)), [total]);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);
  const goto = useCallback((i) => setIdx(i), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); }
      else if (e.key === "Home") { setIdx(0); }
      else if (e.key === "End") { setIdx(total - 1); }
      else if (e.key === "Escape") { setShowIndex((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, total]);

  const Current = SLIDES[idx].component;

  return (
    <div className="h-screen w-screen overflow-hidden bg-slate-950 text-slate-100 selection:bg-brand-orange-400/30 flex flex-col">
      {/* Background */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_-10%,rgba(255,117,31,0.18),transparent_55%),radial-gradient(circle_at_85%_30%,rgba(133,197,54,0.14),transparent_55%),radial-gradient(circle_at_50%_120%,rgba(14,165,233,0.12),transparent_55%)]" />
        <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.4)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.4)_1px,transparent_1px)] [background-size:48px_48px]" />
      </div>

      {/* Top bar */}
      <header className="relative z-20 border-b border-white/5 backdrop-blur-md bg-slate-950/40">
        <div className="max-w-full px-6 py-3 flex items-center justify-between">
          <button onClick={() => goto(0)} className="flex items-center gap-2.5" data-testid="presentacion-home-btn">
            <img src="/assets/sigsaop-logo-light.svg?v=5" alt="SIGSAOP" className="h-9 w-auto object-contain" />
          </button>
          <div className="flex items-center gap-2 text-xs">
            <button onClick={() => setShowIndex((v) => !v)} className="hidden md:inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-slate-300 hover:bg-white/5 transition" data-testid="presentacion-index-btn">
              <Stack size={14} /> Índice
            </button>
            <Link to="/login" className="rounded-md border border-white/10 px-3 py-1.5 text-slate-300 hover:bg-white/5 transition" data-testid="presentacion-login-btn">
              Iniciar sesión
            </Link>
          </div>
        </div>
      </header>

      {/* Slide content */}
      <main className="relative z-10 flex-1 overflow-hidden flex">
        <div className="flex-1 overflow-y-auto" data-testid={`slide-${idx + 1}`}>
          <div key={idx} className="h-full animate-[fadeIn_0.4s_ease-out]">
            <Current />
          </div>
        </div>
      </main>

      {/* Bottom nav */}
      <footer className="relative z-20 border-t border-white/5 backdrop-blur-md bg-slate-950/60">
        <div className="max-w-full px-4 md:px-6 py-3 flex items-center justify-between gap-4">
          <button
            onClick={prev}
            disabled={idx === 0}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200 hover:bg-white/5 transition disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="slide-prev-btn"
          >
            <CaretLeft size={16} weight="bold" /> <span className="hidden md:inline">Anterior</span>
          </button>

          {/* Dots */}
          <div className="hidden md:flex items-center gap-1.5 flex-1 justify-center">
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                onClick={() => goto(i)}
                aria-label={s.title}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-brand-orange-500" : "w-1.5 bg-white/20 hover:bg-white/40"}`}
                data-testid={`slide-dot-${i + 1}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 font-mono whitespace-nowrap">
              {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
            <button
              onClick={next}
              disabled={idx === total - 1}
              className="inline-flex items-center gap-2 rounded-full bg-brand-orange-500 hover:bg-brand-orange-600 px-4 py-2 text-sm font-medium text-white transition disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="slide-next-btn"
            >
              <span className="hidden md:inline">Siguiente</span> <CaretRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      </footer>

      {/* Index overlay */}
      {showIndex && (
        <div className="fixed inset-0 z-40 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShowIndex(false)}>
          <div className="max-w-3xl w-full rounded-2xl border border-white/10 bg-slate-900 p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-bold">Índice de la presentación</h3>
              <button onClick={() => setShowIndex(false)} className="text-slate-400 hover:text-white"><CloseIcon size={20} /></button>
            </div>
            <ul className="space-y-2">
              {SLIDES.map((s, i) => (
                <li key={s.id}>
                  <button
                    onClick={() => { goto(i); setShowIndex(false); }}
                    className={`w-full text-left flex items-center gap-3 rounded-lg px-3 py-2.5 transition ${i === idx ? "bg-brand-orange-500/15 border border-brand-orange-400/30 text-white" : "hover:bg-white/5 text-slate-300"}`}
                  >
                    <span className="font-mono text-xs text-slate-500 w-6">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm">{s.title}</span>
                    {i === idx && <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-brand-orange-300">Actual</span>}
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-4 border-t border-white/10 text-xs text-slate-400">
              Atajos: <kbd className="px-1.5 py-0.5 rounded bg-white/10 mx-1">←</kbd><kbd className="px-1.5 py-0.5 rounded bg-white/10 mx-1">→</kbd> navegar &nbsp;·&nbsp; <kbd className="px-1.5 py-0.5 rounded bg-white/10 mx-1">Esc</kbd> índice
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
